/**
 * Confirm deliveries — ONE step. Received quantities and drop-off temperatures
 * are entered side by side in a single table per mealtime (PROD-1279 D1);
 * submitting records each shipment via the existing receive-delivery endpoint,
 * then calls `onComplete`.
 *
 * Hosted by the Production Records page (the "Transport logs" task, one flow
 * per un-received delivery date — PROD-1277) and by the record detail page's
 * catch-up dialog, optionally filtered to ONE mealtime. It is a receiving
 * action and keys on the DELIVERY date: one arrival time, one temperature
 * per aggregated row, weekly milk as one drop.
 *
 * Rows are AGGREGATED across shipments (same vendor + item + portion +
 * temperature class = one row): campuses that split deliveries by group
 * receive one shipment per homeroom/group, but the cafeteria counts the
 * combined stack once.
 * Confirmed totals fan back out to the per-shipment lines on submit
 * (`distributeReceivedTotal`), so the wire contract is unchanged.
 *
 * The table is the shared `RecordEntryGrid` — the same component the chef's
 * Temperature logs and the Service records task render. A count or reading the
 * cafeteria logged pre-fills its box and is marked beside it (`CafeteriaModeTip`).
 *
 * A school that filled the printed sheet by hand can photograph it instead of
 * retyping it (PROD-1279 D25/D26): `SheetScanCard` sends up to six pages in one
 * request and `handleScan` pushes what comes back through this flow's OWN
 * setters. A scanned value is then indistinguishable from a typed one apart
 * from two marks: an amber `check` ring on a cell the model was unsure about,
 * and PHOTO provenance on a temperature, which rides to the server as
 * `deliverySource` / `deliveryConfidence`.
 *
 * NOT A NATIVE `<form>` (PROD-1279 ruling E1, overriding `apps/ordo/CLAUDE.md`
 * § Forms for this file). A form's Enter-to-submit would confirm a delivery
 * from any count box the moment the gates pass, and "Received by" is prefilled.
 * Every button here is an explicit `type="button"` with an explicit `onClick`.
 */

import { Button } from '@ordo/ui/button'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  type CafeteriaDeliveryWire,
  type Mealtime,
  useCafeteriaDeliveriesQuery,
  useReceiveDeliveryMutation,
} from '@/api/cafeteria'
import { type ScanSheetResponse, useScanSheetMutation } from '@/api/sheet-scan'
import { CafeteriaModeTip } from '@/components/cafeteria/cafeteria-mode-tip'
import { CafeteriaWorkflowHeader } from '@/components/cafeteria/workflow-header'
import {
  RecordEntryCardHeader,
  type RecordEntryColumn,
  RecordEntryGrid,
  type RecordEntrySection,
} from '@/components/production/record-entry-grid'
import { DeliveryCard } from '@/components/production/spreadsheet/record-step-cards'
import { type ScanApplySummary, SCHOOL_SHEET_SCAN_COPY, SheetScanCard } from '@/components/tasks/sheet-scan-card'
import {
  type AggregatedDeliveryRow,
  aggregateDeliveryRows,
  distributeReceivedTotal,
  type MilkReceiptGroup,
  splitMilkDeliveries,
  tempCheckRequired,
} from '@/lib/cafeteria/aggregate-deliveries'
import { cafeteriaDayViewState } from '@/lib/cafeteria/day-view'
import { formatRelativeServiceDateLabel, MEALTIME_LABELS, MEALTIME_ORDER } from '@/lib/cafeteria/format'
import { combineReadingInstant, defaultReadingTime } from '@/lib/cafeteria/reading-time'
import {
  applyTransportScan,
  isScanAbort,
  parseTimeReading,
  remapPhotoIndices,
  SCAN_TIMEOUT_MESSAGE,
  type ScanCellFlag,
  type ScanProvenance,
} from '@/lib/sheet-scan/apply-result'
import { buildTransportScanRows, type TransportScanTarget } from '@/lib/sheet-scan/build-request'
import { filesToImagePayloads } from '@/lib/sheet-scan/images'
import { receiveDraftKey, useCafeteriaReceiveDraftStore } from '@/stores/cafeteria-receive-draft'
import { useProfile } from '@/stores/profile'

/**
 * `Temp Drop-Off` is the chef's exact header and `drop-off temperature` its
 * exact input label: this is the SAME physical reading the chef records at the
 * other end of the truck, so it must not read as a second, different thing.
 */
const TRANSPORT_COLUMNS: ReadonlyArray<RecordEntryColumn> = [
  // 9rem = the provenance mark (1.5rem) + gap + the box (4rem) + slack.
  { key: 'received', header: 'Received', kind: 'count', width: '9rem', inputLabel: 'received' },
  { key: 'temp', header: 'Temp Drop-Off', kind: 'temp', width: '13rem', inputLabel: 'drop-off temperature' },
]

/** Vendor + age ranges under an item, when the day has more than one of either. */
function rowDetail(row: AggregatedDeliveryRow): string | null {
  const detail = [row.vendorName, ...row.ageRangeNames].filter(Boolean).join(', ')
  return detail === '' ? null : detail
}

/** One shipment line's drop-off reading, with where the number came from. */
interface TempLineEntry {
  value: number
  source: 'MANUAL' | 'PHOTO'
  confidence: 'high' | 'low' | null
}

/** The `HH:MM` a previous session persisted for this task, or `''` when none. */
function storedReadingTime(draftKey: string): string {
  return useCafeteriaReceiveDraftStore.getState().getDraft(draftKey).readingTime
}

/** A reading plus its provenance; no scan mark for the row means a hand entry. */
function tempLineEntry(value: number, mark: ScanProvenance[string] | undefined): TempLineEntry {
  return mark
    ? { value, source: mark.source, confidence: mark.confidence }
    : { value, source: 'MANUAL', confidence: null }
}

/**
 * The `check`-flag set after a scan, keyed `${rowKey}:${field}` — the same keys
 * the grid cells below look themselves up with, and the same keys the flow
 * clears when the worker edits that cell.
 *
 * A re-read supersedes the previous one for every cell it FILLED: a number the
 * model read confidently the second time loses its amber ring. A cell the new
 * photos never reached keeps whatever the last scan said about it, so a
 * one-page re-shoot cannot silently clear a warning about a different page.
 */
function nextTransportFlags(
  prev: ReadonlySet<string>,
  result: ScanSheetResponse,
  index: ReadonlyMap<string, TransportScanTarget>,
  flags: readonly ScanCellFlag[],
): Set<string> {
  const next = new Set(prev)
  for (const row of result.rows) {
    const target = index.get(row.rowId)
    if (!target) {
      continue
    }
    for (const [field, cell] of Object.entries(row.cells)) {
      if (cell.value !== null) {
        next.delete(`${target.key}:${field}`)
      }
    }
  }
  for (const flag of flags) {
    next.add(`${flag.rowKey}:${flag.field}`)
  }
  return next
}

export interface ConfirmDeliveriesFlowProps {
  locationId: string
  /** DELIVERY axis — this flow is a receiving action, it follows the truck. */
  deliveryDate: string
  /** When set, only this mealtime's deliveries are shown and submitted. */
  mealtime?: Mealtime | null
  /** Dialog-hosted: fill the container instead of the viewport. */
  embedded?: boolean
  /**
   * Second header line under "Transport logs". The Production Records host
   * names the campus and the service day here — the fullscreen dialog hides
   * the page, so the wizard has to say which record it is filling.
   */
  subtitle?: string
  /** "Save for later" — leave without submitting (draft persists). */
  onExit: () => void
  /** All pending deliveries confirmed (or nothing was pending). */
  onComplete: () => void
}

function groupByMealtime(
  deliveries: CafeteriaDeliveryWire[],
): Array<{ mealtime: Mealtime; rows: AggregatedDeliveryRow[] }> {
  const map = new Map<Mealtime, CafeteriaDeliveryWire[]>()
  for (const d of deliveries) {
    const list = map.get(d.mealtime) ?? []
    list.push(d)
    map.set(d.mealtime, list)
  }
  return MEALTIME_ORDER.filter((m) => map.has(m)).map((mealtime) => ({
    mealtime,
    rows: aggregateDeliveryRows(map.get(mealtime) ?? []),
  }))
}

export function ConfirmDeliveriesFlow({
  locationId,
  deliveryDate,
  mealtime = null,
  embedded = false,
  subtitle,
  onExit,
  onComplete,
}: ConfirmDeliveriesFlowProps) {
  const { profile } = useProfile()

  const deliveriesQuery = useCafeteriaDeliveriesQuery({ schoolLocationId: locationId, deliveryDate })
  // The mealtime filter applies at the source: every derived structure —
  // groups, milk sections, the submit loop — sees only the filtered set.
  const deliveries = useMemo(() => {
    const all = deliveriesQuery.data?.deliveries ?? []
    return mealtime ? all.filter((d) => d.mealtime === mealtime) : all
  }, [deliveriesQuery.data, mealtime])
  // Weekly milk leaves the mealtime groups and confirms as one per-vendor
  // "Milk" section (per-SKU totals, ONE temperature check). The submit loop
  // below still iterates the (filtered) deliveries array, so milk-only weekly
  // shipments get received alongside everything else.
  const { mealDeliveries, milkGroups } = useMemo(() => splitMilkDeliveries(deliveries), [deliveries])
  const grouped = useMemo(() => groupByMealtime(mealDeliveries), [mealDeliveries])

  // Local edits seeded from the persisted "Save for later" draft so they
  // survive navigating away + back. Only user-touched keys live in the maps;
  // untouched lines fall back to the server prefill (qtyFor / deliveryTempDefault).
  const draftKey = receiveDraftKey(locationId, deliveryDate)
  const [qty, setQty] = useState<Record<string, number>>(
    () => useCafeteriaReceiveDraftStore.getState().getDraft(draftKey).qty,
  )
  const [temp, setTemp] = useState<Record<string, number | null>>(
    () => useCafeteriaReceiveDraftStore.getState().getDraft(draftKey).temp,
  )
  // "Time reading" — when the temperatures were taken. A draft wins; otherwise
  // a truck being received TODAY opens on the current clock and a past truck
  // opens blank (`defaultReadingTime`), because stamping "now" on a catch-up
  // receipt would file a fiction on a compliance record.
  const [readingTime, setReadingTimeState] = useState<string>(
    () => storedReadingTime(draftKey) || defaultReadingTime(deliveryDate),
  )
  /**
   * The default above is a SUGGESTION on screen, never an entry. Until the
   * worker (or a scan) sets the time, nothing about it is persisted: otherwise
   * merely OPENING today's truck writes a draft, the Production Records task
   * flips from "Get started" to "Continue" with nothing entered, and the clock
   * time of that glance is what gets restored — and stamped as `arrivedAt` —
   * when the truck is actually received hours later.
   *
   * A time already in the draft was set by a worker, so it stays persisted.
   */
  const readingTimeTouchedRef = useRef(storedReadingTime(draftKey) !== '')
  const [submitting, setSubmitting] = useState(false)
  // Delivery signature — the typed name printed in the production record's
  // "Delivery" signature block. Deliberate (not silently derived): defaults
  // to the signed-in user's name but stays editable; null = untouched.
  const [receivedByInput, setReceivedByInput] = useState<string | null>(null)
  // `${rowKey}:${field}` for every cell a photo filled at LOW confidence. The
  // grid rings those amber; editing the cell clears its key, because the value
  // is then the worker's, not the model's.
  const [scanFlags, setScanFlags] = useState<Set<string>>(new Set())
  // Per row/group temperature provenance, consumed by the submit loop below.
  // Absent = the reading was typed (or pre-filled), i.e. MANUAL.
  const [scanProvenance, setScanProvenance] = useState<ScanProvenance>({})

  const receiveMutation = useReceiveDeliveryMutation()
  const scanMutation = useScanSheetMutation()

  const allRows = useMemo(() => grouped.flatMap((group) => group.rows), [grouped])

  // Persist the draft on every edit (mirrors the serve surface). An untouched
  // default time is not an edit, so it goes as `''` — which `saveDraft` reads
  // as "nothing to keep" when the maps are empty too.
  useEffect(() => {
    useCafeteriaReceiveDraftStore.getState().saveDraft(draftKey, {
      qty,
      temp,
      readingTime: readingTimeTouchedRef.current ? readingTime : '',
    })
  }, [draftKey, qty, temp, readingTime])

  /** The worker typed a time, or a scan read one off the sheet: now it persists. */
  function setReadingTime(next: string): void {
    readingTimeTouchedRef.current = true
    setReadingTimeState(next)
  }

  const receiverNameDefault = [profile?.user?.firstName, profile?.user?.lastName].filter(Boolean).join(' ')
  const receiverName = (receivedByInput ?? receiverNameDefault).trim().slice(0, 80)
  // The flow runs for past trucks from the Production Records page, so the
  // copy names the delivery day instead of assuming "today".
  const dayLabel = formatRelativeServiceDateLabel(deliveryDate)
  const dayWord = dayLabel === 'Today' || dayLabel === 'Yesterday' ? dayLabel.toLowerCase() : `on ${dayLabel}`
  // The host's subtitle is "{campus} · {day}". The scan's context label wants
  // the campus alone — the date travels separately — and falls back to the id
  // when the flow is mounted without a subtitle (the record detail dialog).
  const campusLabel = subtitle?.split('·')[0]?.trim() || locationId

  function qtyFor(row: AggregatedDeliveryRow): number {
    return qty[row.key] ?? row.receivedDefaultTotal
  }

  function tempFor(row: AggregatedDeliveryRow): number | null {
    return row.key in temp ? (temp[row.key] ?? null) : row.tempDefault
  }

  /** One temperature for the whole milk group — keyed by the group, not per row. */
  function milkTempFor(group: MilkReceiptGroup): number | null {
    return group.key in temp ? (temp[group.key] ?? null) : group.tempDefault
  }

  function flagFor(rowKey: string, field: string): 'check' | undefined {
    return scanFlags.has(`${rowKey}:${field}`) ? 'check' : undefined
  }

  /**
   * The worker just typed in this cell, so the model's doubt about it is moot.
   * A temperature also loses its PHOTO provenance: what ships is now a hand
   * entry, and `deliverySource` has to say so.
   */
  function clearScanMarks(rowKey: string, field: string, alsoProvenance = false): void {
    const marked = `${rowKey}:${field}`
    setScanFlags((prev) => {
      if (!prev.has(marked)) {
        return prev
      }
      const next = new Set(prev)
      next.delete(marked)
      return next
    })
    if (alsoProvenance) {
      setScanProvenance((prev) => {
        if (!(rowKey in prev)) {
          return prev
        }
        const next = { ...prev }
        delete next[rowKey]
        return next
      })
    }
  }

  /** The Received box for one aggregated row — the same cell in meal and milk sections. */
  function receivedCell(row: AggregatedDeliveryRow) {
    return {
      kind: 'count' as const,
      value: qtyFor(row),
      flag: flagFor(row.key, 'receivedQuantity'),
      onValueChange: (next: string) => {
        clearScanMarks(row.key, 'receivedQuantity')
        setQty((prev) => ({ ...prev, [row.key]: Number(next) }))
      },
      tip:
        row.cafeteriaCountedTotal !== null ? (
          <CafeteriaModeTip kind="logged" label={`counted ${row.cafeteriaCountedTotal}`} />
        ) : null,
    }
  }

  /**
   * Cells a sheet photo could fill: one count per aggregated row, one reading
   * per row that takes one, and for a milk drop one shared reading plus a count
   * per SKU. This is the denominator in the card's "Filled X of Y fields".
   */
  const scanFieldTotal =
    grouped.reduce(
      (total, group) =>
        total + group.rows.reduce((sum, row) => sum + 1 + (tempCheckRequired(row.temperatureClass) ? 1 : 0), 0),
      0,
    ) + milkGroups.reduce((total, group) => total + 1 + group.rows.length, 0)

  /**
   * Read the printed transport sheet and top the grid up (D26, plan 03 §E.1).
   * Nothing is submitted here: every extracted number lands in the same state a
   * keystroke would, for the worker to check before confirming.
   */
  async function handleScan(files: File[]): Promise<ScanApplySummary> {
    const prepared = await filesToImagePayloads(files)
    if (prepared.payloads.length === 0) {
      // Every photo was refused before it left the device (wrong format, too
      // big): name the first reason rather than a generic failure.
      throw new Error(prepared.errors[0] ?? SCHOOL_SHEET_SCAN_COPY.errorFallback)
    }
    const { rows, index } = buildTransportScanRows(grouped, milkGroups)
    let response: ScanSheetResponse
    try {
      response = await scanMutation.mutateAsync({
        schoolLocationId: locationId,
        serviceDate: deliveryDate,
        task: 'transport',
        contextLabel: campusLabel,
        serviceDateLabel: dayLabel,
        images: prepared.payloads,
        rows,
      })
    } catch (err) {
      if (isScanAbort(err)) {
        throw new Error(SCAN_TIMEOUT_MESSAGE, { cause: err })
      }
      throw err
    }
    // The server numbers its photos by what it RECEIVED; put the worker's own
    // page numbers back before anything phrases a "Photo N" warning.
    const result = remapPhotoIndices(response, prepared.sourceIndices)
    const applied = applyTransportScan(result, index)
    // MERGE the scanned cells into the LATEST state, never replace the map with
    // a pre-scan snapshot: the grid stays live while the photos are read (up to
    // 120s), so a count typed meanwhile has to survive.
    setQty((prev) => ({ ...prev, ...applied.qtyPatch }))
    setTemp((prev) => ({ ...prev, ...applied.tempPatch }))
    setScanProvenance((prev) => ({ ...prev, ...applied.provenance }))
    setScanFlags((prev) => nextTransportFlags(prev, result, index, applied.flags))
    // The sheet's own time box, but never over a time already on screen.
    const scannedTime = parseTimeReading(result.timeReadingText)
    const timeReadingApplied = scannedTime !== null && readingTime === ''
    if (scannedTime !== null && timeReadingApplied) {
      setReadingTime(scannedTime)
    }
    return {
      ...applied.summary,
      // A photo refused on the device is as much a "check this" as a photo the
      // model refused, so both kinds of line reach the card together.
      warnings: [...prepared.errors, ...applied.summary.warnings],
      // The milk DROP row carries a reading but no count box of its own, so a
      // stray count read against it must not print "Filled 7 of 6 fields".
      filledCount: Math.min(applied.summary.filledCount, scanFieldTotal),
      totalCount: scanFieldTotal,
      timeReadingApplied,
    }
  }

  const sections: RecordEntrySection[] = [
    ...grouped.map((group) => ({
      key: group.mealtime,
      header: <RecordEntryCardHeader label={MEALTIME_LABELS[group.mealtime]} />,
      emptyText: 'No items on this truck.',
      rows: group.rows.map((row) => ({
        key: row.key,
        label: row.label,
        legend: rowDetail(row),
        temperatureClass: row.temperatureClass,
        cells: {
          received: receivedCell(row),
          temp: {
            kind: 'temp' as const,
            offered: tempCheckRequired(row.temperatureClass),
            value: tempFor(row),
            flag: flagFor(row.key, 'dropOffTemperatureF'),
            onChange: (next: number | null) => {
              clearScanMarks(row.key, 'dropOffTemperatureF', true)
              setTemp((prev) => ({ ...prev, [row.key]: next }))
            },
            reading: row.cafeteriaTemperature,
            submitted: null,
          },
        },
      })),
    })),
    // ONE temperature for the whole milk drop — cartons arrive as one chilled
    // stack, so per-SKU temps would be theater. The SKUs are sub-rows of that
    // single reading, each with its own count.
    ...milkGroups.map((group) => ({
      key: group.key,
      header: (
        <RecordEntryCardHeader
          label={group.vendorName ? `Milk — ${group.vendorName}` : 'Milk'}
          note={
            <span className="text-xs font-normal text-muted-foreground">Weekly delivery — counts cover the week</span>
          }
        />
      ),
      emptyText: 'No milk on this delivery.',
      rows: [
        {
          key: group.key,
          label: 'Milk at receipt',
          legend: 'One reading for the whole delivery',
          temperatureClass: 'CHILLED' as const,
          cells: {
            temp: {
              kind: 'temp' as const,
              offered: true,
              value: milkTempFor(group),
              flag: flagFor(group.key, 'dropOffTemperatureF'),
              onChange: (next: number | null) => {
                clearScanMarks(group.key, 'dropOffTemperatureF', true)
                setTemp((prev) => ({ ...prev, [group.key]: next }))
              },
              reading: group.cafeteriaTemperature,
              submitted: null,
            },
          },
          subRows: group.rows.map((row) => ({
            key: row.key,
            legend: row.label,
            cells: { received: receivedCell(row) },
          })),
        },
      ],
    })),
  ]

  async function handleSubmit(): Promise<void> {
    if (deliveries.length === 0) {
      onComplete()
      return
    }
    setSubmitting(true)
    // The school's own "Time reading" is the arrival time; an empty box keeps
    // the previous behaviour of stamping the moment of submission.
    const arrivedAt = combineReadingInstant(deliveryDate, readingTime) ?? new Date().toISOString()
    // Fan each aggregated row's confirmed total + temp back out to its
    // constituent shipment lines, then submit once per shipment.
    const qtyByLine = new Map<string, number>()
    // Per-line drop-off reading and where it came from. A reading a photo
    // produced ships as PHOTO with the model's confidence; everything else
    // (typed, pre-filled, untouched) stays MANUAL with no confidence, which is
    // exactly the body this endpoint has always received (D26, plan 03 §E.3).
    const tempByLine = new Map<string, TempLineEntry>()
    for (const row of allRows) {
      for (const part of distributeReceivedTotal(qtyFor(row), row.parts)) {
        qtyByLine.set(part.shipmentLineId, part.receivedQuantity)
      }
      const effectiveTemp = tempFor(row)
      if (effectiveTemp != null && !Number.isNaN(effectiveTemp)) {
        const entry = tempLineEntry(effectiveTemp, scanProvenance[row.key])
        for (const part of row.parts) {
          tempByLine.set(part.shipmentLineId, entry)
        }
      }
    }
    // Milk groups: per-SKU totals fan back like any row; the ONE group temp
    // stamps every constituent line so the per-shipment receive API's
    // per-line temp requirement is satisfied for milk-only shipments too.
    for (const group of milkGroups) {
      const groupTemp = milkTempFor(group)
      const groupEntry = groupTemp == null ? null : tempLineEntry(groupTemp, scanProvenance[group.key])
      for (const row of group.rows) {
        for (const part of distributeReceivedTotal(qtyFor(row), row.parts)) {
          qtyByLine.set(part.shipmentLineId, part.receivedQuantity)
        }
        if (groupEntry && groupTemp != null && !Number.isNaN(groupTemp)) {
          for (const part of row.parts) {
            tempByLine.set(part.shipmentLineId, groupEntry)
          }
        }
      }
    }
    // Only shipments awaiting receipt are submittable — RECEIVED re-posts and
    // VOIDED (zero-line) shipments are rejected server-side and would fail the
    // whole batch.
    const pending = deliveries.filter((d) => d.status === 'NEEDS_RECEIPT')
    if (pending.length === 0) {
      setSubmitting(false)
      onComplete()
      return
    }
    const results = await Promise.allSettled(
      pending.map((d) => {
        const lineQuantities = d.lines.map((line) => ({
          shipmentLineId: line.shipmentLineId,
          receivedQuantity: qtyByLine.get(line.shipmentLineId) ?? line.receivedQuantity ?? line.plannedQuantity,
        }))
        const lineTemperatureLogs = d.lines
          .map((line) => {
            const effective = tempByLine.get(line.shipmentLineId)
            return effective == null
              ? null
              : {
                  shipmentLineId: line.shipmentLineId,
                  deliveryTemperatureF: effective.value,
                  deliverySource: effective.source,
                  // Only a photo has a confidence to report. A MANUAL line
                  // therefore sends exactly the three keys it always has.
                  ...(effective.confidence === null ? {} : { deliveryConfidence: effective.confidence }),
                }
          })
          .filter((x): x is NonNullable<typeof x> => x !== null)
        return receiveMutation.mutateAsync({
          schoolLocationId: locationId,
          shipmentId: d.shipmentId,
          arrivedAt,
          receiverName,
          lineQuantities,
          lineTemperatureLogs,
        })
      }),
    )
    setSubmitting(false)
    const failed = results.filter((r) => r.status === 'rejected').length
    if (failed > 0) {
      toast.error(`${failed} deliver${failed === 1 ? 'y' : 'ies'} could not be confirmed.`)
      return
    }
    useCafeteriaReceiveDraftStore.getState().clearDraft(draftKey)
    toast.success('Deliveries confirmed.')
    onComplete()
  }

  // A write surface with a date-keyed draft (`draftKey`): never render the
  // previous day's rows that `keepPreviousData` holds, or a temperature typed
  // against them would land in the new day's draft — hence `allowStale: false`.
  const { showSkeleton: loading } = cafeteriaDayViewState(
    {
      isPending: deliveriesQuery.isPending,
      isPlaceholderData: deliveriesQuery.isPlaceholderData,
      hasData: Boolean(deliveriesQuery.data),
    },
    { allowStale: false },
  )

  // Every aggregated row — plus one per milk group — needs a temperature
  // reading before the day can be confirmed; the submit button stays disabled
  // until then.
  const allTempsSet =
    allRows.every((row) => {
      // AMBIENT rows take no reading (rendered "N/A") and never gate submit.
      if (!tempCheckRequired(row.temperatureClass)) {
        return true
      }
      const effective = tempFor(row)
      return effective != null && !Number.isNaN(effective)
    }) &&
    milkGroups.every((group) => {
      const effective = milkTempFor(group)
      return effective != null && !Number.isNaN(effective)
    })

  // `Received by` and the time the readings were taken are the NEXT CARD on
  // the canvas under the table — the same `Delivery` card the production
  // record input renders — followed by the one primary. Never a bar pinned
  // under the scroll container (Abner, 2026-10-09).
  const signCards =
    !loading && deliveries.length > 0 ? (
      <>
        <div className="mt-6">
          <DeliveryCard
            hasTrucks
            receiverName={receivedByInput ?? receiverNameDefault}
            onReceiverNameChange={setReceivedByInput}
            time={readingTime}
            onTimeChange={setReadingTime}
            deliveryDates={[deliveryDate]}
            disabled={submitting}
          />
        </div>
        <div className="mt-8 flex flex-col gap-2 md:flex-row md:justify-end">
          <Button
            type="button"
            className="w-full md:w-auto"
            onClick={() => void handleSubmit()}
            disabled={submitting || !allTempsSet || receiverName.length === 0}>
            {submitting ? 'Confirming…' : 'Confirm deliveries'}
          </Button>
        </div>
      </>
    ) : null

  return (
    <div
      className={embedded ? 'flex h-full min-h-0 flex-1 flex-col bg-background' : 'flex h-dvh flex-col bg-background'}>
      <CafeteriaWorkflowHeader
        title="Transport logs"
        subtitle={subtitle}
        actions={
          <Button type="button" variant="outline" onClick={onExit}>
            Save for later
          </Button>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto" data-record-canvas>
        <div className="mx-auto w-full min-w-0 max-w-3xl px-4 py-6 md:px-6 md:py-8">
          <h2 className="text-2xl font-semibold tracking-normal">Confirm receipt of deliveries</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review the meals delivered {dayWord}. Confirm the quantities received and the temperature of each item at
            drop-off (°F).
          </p>

          {/* Above the table, below the heading block, full width — the chef
              precedent. Only where there is something to fill. */}
          {!loading && deliveries.length > 0 ? (
            <div className="mt-6">
              <SheetScanCard
                copy={SCHOOL_SHEET_SCAN_COPY}
                multiple
                maxPhotos={6}
                onExtract={handleScan}
                disabled={submitting}
              />
            </div>
          ) : null}

          {loading ? (
            <div className="mt-6 space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-40 w-full rounded-lg" />
            </div>
          ) : deliveries.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed bg-background p-6 text-center text-sm text-muted-foreground">
              No deliveries are scheduled for {dayWord}.
            </div>
          ) : (
            <div className="mt-6">
              <RecordEntryGrid columns={TRANSPORT_COLUMNS} sections={sections} />
            </div>
          )}
          {signCards}
        </div>
      </div>
    </div>
  )
}
